import { selectParticipantRowHidden } from "../../room/state/stage";
import {
  memo,
  useCallback,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Ellipsis, HeadphoneOff, MicOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useRoomController, useRoomState } from "../../room/RoomContext";
import { initials } from "../../room/initials";
import {
  animateParticipantBadges,
  createBadgeState,
  type BadgeRefs,
} from "../../room/participant-badges";
import {
  participantCloudKey,
  participantCloudSpacing,
  reconcileCloudEntries,
  type CloudEntry,
} from "../../room/participant-cloud";
import { cn } from "../../lib/utils";

// Initials stand in for the picture, so like its empty alt they are painted
// (CSS content) rather than text: the orb's name is the person, not "AL".
function OrbAvatar({ name, picture }: { name: string; picture: string }) {
  const [failedPicture, setFailedPicture] = useState("");
  const showPicture = picture && failedPicture !== picture;
  return (
    <span
      className="participant-cloud-avatar"
      data-initials={showPicture ? undefined : initials(name)}
    >
      {showPicture ? (
        <img
          src={picture}
          alt=""
          referrerPolicy="no-referrer"
          onError={() => setFailedPicture(picture)}
        />
      ) : null}
    </span>
  );
}

const ParticipantOrb = memo(function ParticipantOrb({
  entry,
  onExit,
}: {
  entry: CloudEntry;
  onExit: (_key: string) => void;
}) {
  const { person, leaving, x, y, offset, motion } = entry;
  const controller = useRoomController();
  const { t } = useTranslation();
  const [joining, setJoining] = useState(true);
  const root = useRef<HTMLButtonElement>(null);
  const refs = useRef<Partial<BadgeRefs>>({});
  const measure = useRef<SVGTextElement>(null);
  const badgeState = useRef(createBadgeState());
  const curveId = `participant-live-curve-${useId()}`;
  const liveLabel = t("common.live");
  const statusLabels = person.connecting
    ? [t("stage.connecting")]
    : [
        // The curved LIVE badge is the orb's only visible text, so its name says it too.
        person.screen && liveLabel,
        person.microphoneOff && t("voice.notSending"),
        person.notListening && t("voice.notListeningToOthers"),
      ].filter(Boolean);
  const avatarLabel = t(
    person.canOpenScreen ? "context.watchParticipantScreen" : "context.participantAvatar",
    { name: person.name },
  );
  const registerVideo = useCallback(
    (video: HTMLVideoElement | null) => {
      controller.registerParticipantCamera(person.id, video);
    },
    [controller, person.id],
  );

  useLayoutEffect(() => {
    const node = root.current!;
    node.classList.toggle("speaking", controller.participantIsSpeaking(person.id));
  });
  useLayoutEffect(
    () =>
      controller.subscribeParticipantSpeaking((id, speaking) => {
        if (id === person.id) root.current?.classList.toggle("speaking", speaking);
      }),
    [controller, person.id],
  );

  useLayoutEffect(() => {
    if (!leaving) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- clears the join state so the exit animation plays before the orb unmounts; restructuring would change the animation timing
    setJoining(false);
    const timer = setTimeout(() => onExit(participantCloudKey(person)), 420);
    return () => clearTimeout(timer);
  }, [leaving, person, onExit]);

  useLayoutEffect(() => {
    let disposed = false;
    const state = badgeState.current;
    const refresh = () => {
      if (disposed) return;
      // SVG text cannot be measured inside a display:none badge. Reveal it
      // before the layout read; the ref animator restores its current opacity.
      if (person.screen) refs.current.live!.hidden = false;
      // The hidden SVG text uses exactly the painted curved label's font. Cache
      // within this effect; language/font changes measure again, never each frame.
      const textWidth = person.screen
        ? measure.current!.getComputedTextLength() || liveLabel.length * 7
        : 0;
      animateParticipantBadges({ ...refs.current, root: root.current! } as BadgeRefs, state, {
        size: person.camera ? 190 : 128,
        screen: person.screen,
        textWidth,
        microphoneOff: person.microphoneOff,
        notListening: person.notListening,
        connecting: person.connecting,
      });
    };
    refresh();
    void document.fonts?.ready.then(refresh);
    return () => {
      disposed = true;
      if (state.frame) cancelAnimationFrame(state.frame);
      // Resume an interrupted transition even if StrictMode repeats the effect.
      state.key = "interrupted";
    };
  }, [
    person.screen,
    person.camera,
    person.microphoneOff,
    person.notListening,
    person.connecting,
    liveLabel,
  ]);

  const style = {
    "--cloud-x": x,
    "--cloud-y": y,
    "--compact-offset": `${offset}px`,
    "--float-delay": `${motion.delay}s`,
    "--float-duration": `${motion.duration}s`,
    "--float-tilt": `${motion.tilt}deg`,
    "--float-x": `${motion.driftX}px`,
    "--float-y": `${motion.driftY}px`,
    "--float-return-x": `${motion.returnX}px`,
    "--float-return-y": `${motion.returnY}px`,
  } as CSSProperties;
  return (
    <button
      ref={root}
      type="button"
      data-peer-id={person.id}
      style={style}
      className={cn(
        "participant-orb",
        person.self && "local",
        person.camera && "has-camera",
        joining && !leaving && "joining",
        leaving && "leaving",
      )}
      aria-label={[avatarLabel, ...statusLabels].join(" · ")}
      title={[person.name, ...statusLabels].join(" · ")}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) return;
        if (event.animationName === "participant-bounce-in") setJoining(false);
        if (leaving && event.animationName === "participant-bounce-out")
          onExit(participantCloudKey(person));
      }}
      onClick={(event) => {
        if (!leaving) {
          event.stopPropagation();
          controller.watchParticipantScreen(person.id);
        }
      }}
      onContextMenu={(event) => {
        if (!leaving) {
          event.preventDefault();
          controller.openStreamContextMenu(person.id, event.clientX, event.clientY);
        }
      }}
    >
      <span className="participant-orb-floating" aria-hidden="true">
        <span
          ref={(node) => {
            refs.current.visual = node!;
          }}
          className="participant-orb-visual"
        >
          <OrbAvatar name={person.name} picture={person.picture} />
          <video
            ref={registerVideo}
            className="participant-cloud-camera"
            autoPlay
            playsInline
            muted
            controls={false}
          />
        </span>
        <span
          ref={(node) => {
            refs.current.live = node!;
          }}
          className="participant-cloud-live"
        >
          <svg
            ref={(node) => {
              refs.current.liveSvg = node!;
            }}
            className="participant-cloud-live-svg"
          >
            <defs>
              <path
                ref={(node) => {
                  refs.current.curve = node!;
                }}
                id={curveId}
              />
            </defs>
            <path
              ref={(node) => {
                refs.current.liveBackground = node!;
              }}
              className="participant-cloud-live-background"
            />
            <text className="participant-cloud-live-text">
              <textPath
                ref={(node) => {
                  refs.current.textPath = node!;
                }}
                href={`#${curveId}`}
              >
                {liveLabel}
              </textPath>
            </text>
            <text ref={measure} className="participant-cloud-live-measure">
              {liveLabel}
            </text>
          </svg>
        </span>
        <span
          ref={(node) => {
            refs.current.voice = node!;
          }}
          className="participant-cloud-voice-status"
        >
          <svg
            ref={(node) => {
              refs.current.voiceSvg = node!;
            }}
            className="participant-cloud-voice-svg"
          >
            <path
              ref={(node) => {
                refs.current.voiceBackground = node!;
              }}
              className="participant-cloud-voice-background"
            />
            <path
              ref={(node) => {
                refs.current.divider = node!;
              }}
              className="participant-cloud-voice-divider"
            />
          </svg>
          <span
            ref={(node) => {
              refs.current.voiceOff = node!;
            }}
            className="participant-cloud-voice-off"
          >
            <MicOff />
          </span>
          <span
            ref={(node) => {
              refs.current.notListening = node!;
            }}
            className="participant-cloud-not-listening"
          >
            <HeadphoneOff />
          </span>
          <span
            ref={(node) => {
              refs.current.connectingIndicator = node!;
            }}
            className="participant-cloud-connecting"
          >
            <Ellipsis />
          </span>
        </span>
        <span className="participant-cloud-name">{person.name}</span>
      </span>
    </button>
  );
});

export function ParticipantCloud() {
  const { t } = useTranslation();
  const people = useRoomState((s) => s.participants.participantCloud);
  const compact = useRoomState((s) => s.stage.streamFocusActive);
  const rowHidden = useRoomState(selectParticipantRowHidden);
  const cloud = useRef<HTMLDivElement>(null);
  const [previous, setPrevious] = useState(people);
  const [entries, setEntries] = useState<CloudEntry[]>(() => reconcileCloudEntries([], people));
  if (previous !== people) {
    setPrevious(people);
    setEntries(reconcileCloudEntries(entries, people));
  }
  const onExit = useCallback((key: string) => {
    setEntries((current) =>
      current.filter((entry) => !entry.leaving || participantCloudKey(entry.person) !== key),
    );
  }, []);
  useLayoutEffect(() => {
    const node = cloud.current!;
    const stage = node.parentElement!;
    const resize = () =>
      node.style.setProperty(
        "--cloud-spacing",
        `${participantCloudSpacing(people, stage.clientWidth, stage.clientHeight)}px`,
      );
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [people]);
  return (
    <div
      ref={cloud}
      id="participantCloud"
      role="group"
      className={cn(
        "participant-cloud",
        compact && "compact",
        compact && rowHidden && "row-hidden",
      )}
      aria-label={t("stage.participants")}
      data-count={people.length}
    >
      {entries.map((entry) => (
        <ParticipantOrb key={participantCloudKey(entry.person)} entry={entry} onExit={onExit} />
      ))}
    </div>
  );
}
